import React, { useEffect, useState } from "react";
import { Avatar, Button, Input, Modal, Skeleton, fmtCoins } from "../../components/ui";
import { DataTable, when } from "../../components/admin/fields";
import useCursorList from "../../components/admin/useCursorList";
import { useToast } from "../../context/ToastContext";
import content from "../../services/api-content";
import "../../styles/content.css";

function UserModal({ id, onClose, onChanged }) {
  const toast = useToast();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => content.admin.users.get(id).then(setD).catch((e) => setErr(e.message));
  useEffect(() => { setD(null); setErr(""); setReason(""); setAmount(""); setNote(""); if (id) load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const u = d?.user;

  const ban = async (banned) => {
    if (banned && !reason.trim()) return setErr("กรุณาระบุเหตุผลการแบน");
    setBusy(true); setErr("");
    try { await content.admin.users.ban(id, banned, reason.trim()); toast(banned ? "แบนผู้ใช้แล้ว" : "ปลดแบนแล้ว", "success"); setReason(""); await load(); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const adjust = async () => {
    const n = Number(amount);
    if (!n || !Number.isFinite(n)) return setErr("ระบุจำนวนคอยน์เป็นตัวเลข (+ เพิ่ม / - ลด)");
    if (!note.trim()) return setErr("กรุณาระบุเหตุผลการปรับคอยน์");
    if (!window.confirm(`${n > 0 ? "เพิ่ม" : "ลด"} ${fmtCoins(Math.abs(n))} คอยน์ให้ ${u?.displayName}?`)) return;
    setBusy(true); setErr("");
    try { const r = await content.admin.users.adjustCoins(id, n, note.trim()); toast(`ปรับแล้ว คงเหลือ ${fmtCoins(r.balance)} คอยน์`, "success"); setAmount(""); setNote(""); await load(); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open={!!id} onClose={onClose} title="จัดการผู้ใช้" size="lg">
      {!d && !err ? <Skeleton height={200} /> : null}
      {u ? (
        <div className="stack">
          <div className="row"><Avatar name={u.displayName} src={u.photoURL} size={52} /><div className="grow"><div className="strong">{u.displayName}</div><div className="muted" style={{ fontSize: 13 }}>@{u.username || "-"} · {u.email}</div></div>
            <span className={`pill ${u.banned ? "pill--bad" : "pill--ok"}`}>{u.banned ? "ถูกแบน" : "ปกติ"}</span></div>
          <div className="kpi"><div className="stat card"><b>{fmtCoins(u.coins)}</b><span>คอยน์คงเหลือ</span></div><div className="stat card"><b>{d.vip?.tier?.name || d.vip?.tier || "-"}</b><span>ระดับ VIP</span></div><div className="stat card"><b>{u.characterCount || 0}</b><span>ตัวละคร</span></div></div>
          {u.banned && u.banReason ? <div className="card">เหตุผลที่แบน: {u.banReason}</div> : null}

          <div className="card">
            <div className="strong mb-8">แบน / ปลดแบน</div>
            <Input aria-label="เหตุผล" placeholder={u.banned ? "หมายเหตุ (ไม่บังคับ)" : "เหตุผลการแบน *"} value={reason} onChange={(e) => setReason(e.target.value)} />
            {u.banned ? <Button onClick={() => ban(false)} disabled={busy}>ปลดแบน</Button> : <Button variant="danger" onClick={() => ban(true)} disabled={busy}>แบนผู้ใช้</Button>}
          </div>
          <div className="card">
            <div className="strong mb-8">ปรับคอยน์</div>
            <div className="form-grid">
              <Input aria-label="จำนวน" type="number" placeholder="จำนวน เช่น 100 หรือ -50" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Input aria-label="เหตุผล" placeholder="เหตุผล * (บันทึกในประวัติ)" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <Button variant="primary" onClick={adjust} disabled={busy}>ยืนยันปรับคอยน์</Button>
          </div>
          <div>
            <div className="strong mb-8">ประวัติคอยน์ล่าสุด</div>
            <DataTable columns={[{ key: "createdAt", label: "เวลา", render: (r) => when(r.createdAt) }, { key: "usageType", label: "ประเภท" }, { key: "amount", label: "จำนวน", render: (r) => <b style={{ color: Number(r.amount ?? r.coinUsed) >= 0 ? "var(--success)" : "var(--danger)" }}>{Number(r.amount ?? r.coinUsed) > 0 ? "+" : ""}{r.amount ?? r.coinUsed}</b> }, { key: "note", label: "หมายเหตุ" }]} rows={d.coinTx || []} empty="ยังไม่มีรายการ" />
          </div>
        </div>
      ) : null}
      {err ? <div className="error-text mt-8" role="alert">{err}</div> : null}
    </Modal>
  );
}

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [sel, setSel] = useState(null);
  const list = useCursorList((cursor) => content.admin.users.list({ q: term, cursor }), [term]);
  const cols = [
    { key: "displayName", label: "ชื่อ", render: (u) => <span className="row" style={{ gap: 8 }}><Avatar name={u.displayName} src={u.photoURL} size={28} />{u.displayName}</span> },
    { key: "email", label: "อีเมล", render: (u) => <span className="muted">{u.email}</span> },
    { key: "coins", label: "คอยน์", render: (u) => fmtCoins(u.coins) },
    { key: "status", label: "สถานะ", render: (u) => <span className={`pill ${u.banned ? "pill--bad" : "pill--ok"}`}>{u.banned ? "แบน" : u.userType === "super_user" ? "แอดมิน" : "ปกติ"}</span> },
    { key: "createdAt", label: "สมัครเมื่อ", render: (u) => when(u.createdAt) },
    { key: "act", label: "", render: (u) => <Button size="sm" onClick={() => setSel(u.id)}>จัดการ</Button> },
  ];
  return (
    <div>
      <form className="row mb-16" onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }}>
        <div className="grow"><Input type="search" aria-label="ค้นหาผู้ใช้" placeholder="ค้นหาชื่อ / อีเมล / username" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 0 }} /></div>
        <Button type="submit">ค้นหา</Button>
      </form>
      <DataTable columns={cols} rows={list.items} loading={list.loading} error={list.error} onRetry={list.reload} hasMore={!!list.next} onMore={list.loadMore} moreLoading={list.more} empty="ไม่พบผู้ใช้" />
      <UserModal id={sel} onClose={() => setSel(null)} onChanged={list.reload} />
    </div>
  );
}
